import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import {
  BASE_URL,
  GET_CART_URL,
  ADD_TO_CART_URL,
  UPDATE_CART_ITEM_URL,
  REMOVE_FROM_CART_URL,
  CLEAR_CART_URL,
  GET_TAX_URL,
} from "../constants";

const getAuthConfig = () => {
  const token = localStorage.getItem("token");
  return {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    withCredentials: true,
  };
};

// Thunk: Fetch cart from backend
export const fetchCart = createAsyncThunk(
  "cart/fetchCart",
  async (_, thunkAPI) => {
    try {
      const response = await axios.get(
        `${BASE_URL}${GET_CART_URL}`,
        getAuthConfig(),
      );
      return response.data; // Backend cart data
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data || "Failed to fetch cart",
      );
    }
  },
);

// Add to Cart
export const addToCart = createAsyncThunk(
  "cart/addToCart",
  async (item, thunkAPI) => {
    try {
      const response = await axios.post(
        `${BASE_URL}${ADD_TO_CART_URL}`,
        item,
        getAuthConfig(),
      );
      thunkAPI.dispatch(fetchCart());
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data || "Failed to add item to cart",
      );
    }
  },
);

// Update Cart Item
export const updateCartItem = createAsyncThunk(
  "cart/updateCartItem",
  async ({ productId, qty }, thunkAPI) => {
    try {
      const response = await axios.put(
        `${BASE_URL}${UPDATE_CART_ITEM_URL}`,
        { productId, qty },
        getAuthConfig(),
      );
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data || "Failed to update item",
      );
    }
  },
);

// Remove from Cart
export const removeFromCart = createAsyncThunk(
  "cart/removeFromCart",
  async (itemId, thunkAPI) => {
    try {
      const response = await axios.delete(
        `${BASE_URL}${REMOVE_FROM_CART_URL(itemId)}`,
        getAuthConfig(),
      );
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data || "Failed to remove item",
      );
    }
  },
);

// Clear Cart
export const clearCartAsync = createAsyncThunk(
  "cart/clearCart",
  async (_, thunkAPI) => {
    try {
      const response = await axios.delete(
        `${BASE_URL}${CLEAR_CART_URL}`,
        getAuthConfig(),
      );
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data || "Failed to clear cart",
      );
    }
  },
);

// Fetch Tax Rate
export const fetchTaxRate = createAsyncThunk(
  "cart/fetchTaxRate",
  async (_, thunkAPI) => {
    try {
      const response = await axios.get(
        `${BASE_URL}${GET_TAX_URL}`,
        getAuthConfig(),
      );
      return response.data.taxPercent;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data || "Failed to fetch tax rate",
      );
    }
  },
);

const cartSlice = createSlice({
  name: "cart",
  initialState: {
    cartItems: [],
    totalPrice: 0,
    itemCount: 0,
    shipping: 0, // Add shipping details
    taxRate: 0, // Tax rate from backend
    isLoading: false,
    error: null,
  },
  reducers: {
    // Update shipping cost dynamically
    // updateShipping: (state, action) => {
    //   state.shipping = action.payload.shipping;
    //   state.totalPrice = state.cartItems.reduce(
    //     (total, item) => total + item.qty * item.price,
    //     0,
    //   );
    //   const tax = state.totalPrice * (state.taxRate / 100);
    //   state.totalPrice += tax + action.payload.shipping;
    // },
    updateShipping: (state, action) => {
      state.shipping = action.payload.shipping; // Only update shipping
    },

    clearCart: (state) => {
      state.cartItems = [];
      state.totalPrice = 0;
      state.itemCount = 0;
      state.shipping = 0; // Reset shipping
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Cart
      .addCase(fetchCart.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchCart.fulfilled, (state, action) => {
        state.isLoading = false;
        state.cartItems = action.payload.cartItems || [];
        state.totalPrice = action.payload.totalPrice || 0;
        state.itemCount = state.cartItems.length;
      })

      .addCase(fetchCart.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Add to Cart
      .addCase(addToCart.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(addToCart.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(addToCart.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(updateCartItem.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updateCartItem.fulfilled, (state, action) => {
        state.isLoading = false;
        state.cartItems = action.payload.cartItems; // Use updated cart items from backend
        state.totalPrice = action.payload.totalPrice; // Use updated total price
        state.itemCount = state.cartItems.length;
      })
      .addCase(updateCartItem.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Remove from Cart
      .addCase(removeFromCart.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(removeFromCart.fulfilled, (state, action) => {
        state.isLoading = false;
        state.cartItems = action.payload.cartItems; // Use updated cart items from backend
        state.totalPrice = action.payload.totalPrice; // Use updated total price
        state.itemCount = state.cartItems.length;
      })
      .addCase(removeFromCart.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Clear Cart
      .addCase(clearCartAsync.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(clearCartAsync.fulfilled, (state) => {
        state.isLoading = false;
        state.cartItems = []; // Reset cart items
        state.totalPrice = 0; // Reset total price
        state.itemCount = 0; // Reset item count
        state.shipping = 0; // Reset shipping
      })
      .addCase(clearCartAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Fetch Tax Rate
      .addCase(fetchTaxRate.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchTaxRate.fulfilled, (state, action) => {
        state.isLoading = false;
        state.taxRate = action.payload;
      })
      .addCase(fetchTaxRate.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const { updateShipping, clearCart } = cartSlice.actions;

export default cartSlice.reducer;

// import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
// import axios from "axios";
// import {
//   BASE_URL,
//   GET_CART_URL,
//   ADD_TO_CART_URL,
//   UPDATE_CART_ITEM_URL,
//   REMOVE_FROM_CART_URL,
//   CLEAR_CART_URL,
//   GET_TAX_URL,
// } from "../constants";

// // Thunk: Fetch cart from backend
// export const fetchCart = createAsyncThunk(
//   "cart/fetchCart",
//   async (_, thunkAPI) => {
//     try {
//       const response = await axios.get(`${BASE_URL}${GET_CART_URL}`, {
//         withCredentials: true,
//       });
//       // const response = await axios.get(GET_CART_URL);
//       return response.data; // Backend cart data
//     } catch (error) {
//       return thunkAPI.rejectWithValue(
//         error.response?.data || "Failed to fetch cart",
//       );
//     }
//   },
// );

// // Thunk: Add item to cart
// export const addToCart = createAsyncThunk(
//   "cart/addToCart",
//   async (item, thunkAPI) => {
//     try {
//       // const response = await axios.post(ADD_TO_CART_URL, item);
//       const response = await axios.post(`${BASE_URL}${ADD_TO_CART_URL}`, item, {
//         withCredentials: true,
//       });
//       thunkAPI.dispatch(fetchCart()); // Sync local cart after adding
//       return response.data;
//     } catch (error) {
//       return thunkAPI.rejectWithValue(
//         error.response?.data || "Failed to add item to cart",
//       );
//     }
//   },
// );

// export const updateCartItem = createAsyncThunk(
//   "cart/updateCartItem",
//   async ({ productId, qty }, thunkAPI) => {
//     try {
//       // const response = await axios.put(UPDATE_CART_ITEM_URL, {
//       //   productId,
//       //   qty,
//       // });
//       const response = await axios.put(
//         `${BASE_URL}${UPDATE_CART_ITEM_URL}`,
//         { productId, qty },
//         { withCredentials: true },
//       );
//       return response.data; // Backend sends updated cart data
//     } catch (error) {
//       return thunkAPI.rejectWithValue(
//         error.response?.data || "Failed to update item",
//       );
//     }
//   },
// );

// export const removeFromCart = createAsyncThunk(
//   "cart/removeFromCart",
//   async (itemId, thunkAPI) => {
//     try {
//       // const response = await axios.delete(REMOVE_FROM_CART_URL(itemId));
//       const response = await axios.delete(
//         `${BASE_URL}${REMOVE_FROM_CART_URL(itemId)}`,
//         { withCredentials: true },
//       );
//       return response.data; // Backend sends updated cart data
//     } catch (error) {
//       return thunkAPI.rejectWithValue(
//         error.response?.data || "Failed to remove item",
//       );
//     }
//   },
// );

// export const clearCartAsync = createAsyncThunk(
//   "cart/clearCart",
//   async (_, thunkAPI) => {
//     try {
//       // const response = await axios.delete(CLEAR_CART_URL);
//       const response = await axios.delete(`${BASE_URL}${CLEAR_CART_URL}`, {
//         withCredentials: true,
//       });
//       return response.data; // Backend sends cleared cart data
//     } catch (error) {
//       return thunkAPI.rejectWithValue(
//         error.response?.data || "Failed to clear cart",
//       );
//     }
//   },
// );

// // Fetch tax rate from the backend
// export const fetchTaxRate = createAsyncThunk(
//   "cart/fetchTaxRate",
//   async (_, thunkAPI) => {
//     try {
//       // const response = await axios.get(GET_TAX_URL);
//       const response = await axios.get(`${BASE_URL}${GET_TAX_URL}`, {
//         withCredentials: true,
//       });
//       return response.data.taxPercent; // Return the tax rate
//     } catch (error) {
//       return thunkAPI.rejectWithValue(
//         error.response?.data || "Failed to fetch tax rate",
//       );
//     }
//   },
// );

// const cartSlice = createSlice({
//   name: "cart",
//   initialState: {
//     cartItems: [],
//     totalPrice: 0,
//     itemCount: 0,
//     shipping: 0, // Add shipping details
//     taxRate: 0, // Tax rate from backend
//     isLoading: false,
//     error: null,
//   },
//   reducers: {
//     // Update shipping cost dynamically
//     // updateShipping: (state, action) => {
//     //   state.shipping = action.payload.shipping;
//     //   state.totalPrice = state.cartItems.reduce(
//     //     (total, item) => total + item.qty * item.price,
//     //     0,
//     //   );
//     //   const tax = state.totalPrice * (state.taxRate / 100);
//     //   state.totalPrice += tax + action.payload.shipping;
//     // },
//     updateShipping: (state, action) => {
//       state.shipping = action.payload.shipping; // Only update shipping
//     },

//     clearCart: (state) => {
//       state.cartItems = [];
//       state.totalPrice = 0;
//       state.itemCount = 0;
//       state.shipping = 0; // Reset shipping
//     },
//   },
//   extraReducers: (builder) => {
//     builder
//       // Fetch Cart
//       .addCase(fetchCart.pending, (state) => {
//         state.isLoading = true;
//       })
//       .addCase(fetchCart.fulfilled, (state, action) => {
//         state.isLoading = false;
//         state.cartItems = action.payload.cartItems || [];
//         state.totalPrice = action.payload.totalPrice || 0;
//         state.itemCount = state.cartItems.length;
//       })

//       .addCase(fetchCart.rejected, (state, action) => {
//         state.isLoading = false;
//         state.error = action.payload;
//       })
//       // Add to Cart
//       .addCase(addToCart.pending, (state) => {
//         state.isLoading = true;
//       })
//       .addCase(addToCart.fulfilled, (state) => {
//         state.isLoading = false;
//       })
//       .addCase(addToCart.rejected, (state, action) => {
//         state.isLoading = false;
//         state.error = action.payload;
//       })
//       .addCase(updateCartItem.pending, (state) => {
//         state.isLoading = true;
//       })
//       .addCase(updateCartItem.fulfilled, (state, action) => {
//         state.isLoading = false;
//         state.cartItems = action.payload.cartItems; // Use updated cart items from backend
//         state.totalPrice = action.payload.totalPrice; // Use updated total price
//         state.itemCount = state.cartItems.length;
//       })
//       .addCase(updateCartItem.rejected, (state, action) => {
//         state.isLoading = false;
//         state.error = action.payload;
//       })
//       // Remove from Cart
//       .addCase(removeFromCart.pending, (state) => {
//         state.isLoading = true;
//       })
//       .addCase(removeFromCart.fulfilled, (state, action) => {
//         state.isLoading = false;
//         state.cartItems = action.payload.cartItems; // Use updated cart items from backend
//         state.totalPrice = action.payload.totalPrice; // Use updated total price
//         state.itemCount = state.cartItems.length;
//       })
//       .addCase(removeFromCart.rejected, (state, action) => {
//         state.isLoading = false;
//         state.error = action.payload;
//       })
//       // Clear Cart
//       .addCase(clearCartAsync.pending, (state) => {
//         state.isLoading = true;
//       })
//       .addCase(clearCartAsync.fulfilled, (state) => {
//         state.isLoading = false;
//         state.cartItems = []; // Reset cart items
//         state.totalPrice = 0; // Reset total price
//         state.itemCount = 0; // Reset item count
//         state.shipping = 0; // Reset shipping
//       })
//       .addCase(clearCartAsync.rejected, (state, action) => {
//         state.isLoading = false;
//         state.error = action.payload;
//       })
//       // Fetch Tax Rate
//       .addCase(fetchTaxRate.pending, (state) => {
//         state.isLoading = true;
//       })
//       .addCase(fetchTaxRate.fulfilled, (state, action) => {
//         state.isLoading = false;
//         state.taxRate = action.payload;
//       })
//       .addCase(fetchTaxRate.rejected, (state, action) => {
//         state.isLoading = false;
//         state.error = action.payload;
//       });
//   },
// });

// export const { updateShipping, clearCart } = cartSlice.actions;

// export default cartSlice.reducer;
